import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchData, postData } from "../redux/slices/apiCallSlice";
import { selectApiUrls } from "../redux/slices/apiUrlSlice";
import { fetchFullJob } from "../redux/slices/fullJobFetchSlice";
import { selectJobDetails } from "../redux/slices/jobDetailsSlice";

const useCopyJob = () => {
  const [status, setStatus] = useState({});
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);

  // Search for jobs by job number
  const searchJobs = async (searchTerm) => {
    setIsSearching(true);
    try {
      const response = await dispatch(fetchData({ 
        url: `${apiUrls.generalProject}` 
      })).unwrap();
      
      const filteredResults = response.filter(job => {
        const jobNumber = (job.JobNumber || job.jobNumber || "").toString().toLowerCase();
        const projectName = (job.ProjectName || job.projectName || "").toString().toLowerCase();
        const term = searchTerm.toLowerCase();
        
        return jobNumber.includes(term) || projectName.includes(term);
      });
      
      setSearchResults(filteredResults || []);
      return filteredResults || [];
    } catch (error) {
      console.error("Error searching for jobs:", error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Preload all data for a source job
  const preloadSourceJob = async (sourceJobId, logs = []) => {
    logs.push(`Preloading data for source job ID: ${sourceJobId} with full job fetch...`);

    try {
      await dispatch(fetchFullJob({ jobId: sourceJobId })).unwrap();

      const state = await dispatch((_, getState) => getState());
      const sourceJobDetails = selectJobDetails(state) || {};

      // Build a copy-job compatible object from centralized full-job state.
      const jobData = {
        generalProject: sourceJobDetails.generalProject || [],
        generalStation: sourceJobDetails.generalStation || [],
        soldToShipTo: sourceJobDetails.soldToShipTo || [],
        priceBreakdown: sourceJobDetails.priceBreakdown || [],
        ownersManual: sourceJobDetails.ownersManual || [],
        hcRequirements: sourceJobDetails.hcRequirements || [],
        skidConstruction: sourceJobDetails.skidConstruction || [],
        pumpManifoldPiping: sourceJobDetails.pumpManifoldPiping || [],
        stationEnclosure: sourceJobDetails.stationEnclosure || [],
        dropPipeComments: sourceJobDetails.dropPipeComments || [],
        dropPipes: sourceJobDetails.dropPipes || [],
        levelControls: sourceJobDetails.levelControls || [],
        fillSources: sourceJobDetails.fillSources || [],
        pumps: sourceJobDetails.pumps || [],
        pumpDetails: sourceJobDetails.pumpDetails || [],
        waterTreatment: sourceJobDetails.waterTreatment || [],
        inletScreen: sourceJobDetails.inletScreen || [],
        inletStrainer: sourceJobDetails.inletStrainer || [],
        dischargeStrainer: sourceJobDetails.dischargeStrainer || [],
        dischargeFilter: sourceJobDetails.dischargeFilter || [],
        roSystem: sourceJobDetails.roSystem || [],
        uvSystem: sourceJobDetails.uvSystem || [],
        fertigation: sourceJobDetails.fertigation || [],
        skyHarvester: sourceJobDetails.skyHarvester || [],
        shipLoose: sourceJobDetails.shipLoose || [],
        unregulatedPower: sourceJobDetails.unregulatedPower || [],
        unregulatedBreakers: sourceJobDetails.unregulatedBreakers || [],
        regulatedPower: sourceJobDetails.regulatedPower || [],
        regulatedBreakers: sourceJobDetails.regulatedBreakers || [],
        sequenceOfOperations: sourceJobDetails.sequenceOfOperations || [],
        electricalOptions: sourceJobDetails.electricalOptions || [],
        telemetry: sourceJobDetails.telemetry || [],
        telemetryAccessories: sourceJobDetails.telemetryAccessories || [],
        selectedPumpOptionList: sourceJobDetails.selectedPumpOptionList || [],
        customOptions: sourceJobDetails.customOptions || [],
        warningReport: sourceJobDetails.warningReport || [],
        changedOptions: sourceJobDetails.changedOptions || [],
        controlLogicOnly: sourceJobDetails.controlLogicOnly || [],
        shipEarlyParent: sourceJobDetails.shipEarlyParent || [],
        shipEarlyItems: sourceJobDetails.shipEarlyItems || [],
        changeOrderParent: sourceJobDetails.changeOrderParent || [],
        changeOrderDetails: sourceJobDetails.changeOrderDetails || [],
      };

      logs.push("Successfully preloaded source job data via full job fetch.");

      let totalRecords = 0;
      let tablesWithData = 0;
      Object.entries(jobData).forEach(([tableName, tableData]) => {
        if (Array.isArray(tableData) && tableData.length > 0) {
          totalRecords += tableData.length;
          tablesWithData++;
          logs.push(`Found ${tableData.length} records for ${tableName}`);
        }
      });

      logs.push(`Found data in ${tablesWithData} tables with a total of ${totalRecords} records to copy.`);

      return jobData;
    } catch (error) {
      console.error("Error preloading source job data:", error);
      logs.push(`Error preloading source job data: ${error.message}`);
      return {};
    }
  };

  // Helper function to convert just the first character of each key to lowercase
  const convertKeysToProperFormat = (obj) => {
    if (!obj || typeof obj !== 'object') return obj;
    
    return Object.keys(obj).reduce((acc, key) => {
      // Convert only the first character to lowercase
      const formattedKey = key.charAt(0).toLowerCase() + key.slice(1);
      
      // Handle nested objects
      if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
        acc[formattedKey] = convertKeysToProperFormat(obj[key]);
      } else {
        acc[formattedKey] = obj[key];
      }
      
      return acc;
    }, {});
  };

  // Copy parent-child related data in the correct order
  const copyParentChildData = async (
    parentUrl, 
    childUrl, 
    sourceParentData, 
    sourceChildData, 
    newJobId, 
    parentApiName, 
    childApiName, 
    logs, 
    apiStatuses, 
    setApiStatus, 
    parentAdditionalData = {}, 
    childLinkFieldName,
    hasChildFieldName = 'hasAccessories' // Default field name that determines if child records should be created
  ) => {
    // Set status for parent
    apiStatuses[parentApiName] = "loading";
    setApiStatus({ ...apiStatuses });
    logs.push(`Processing ${parentApiName} data (parent table)...`);
    
    try {
      // First create the parent record(s)
      const parentResults = [];
      
      if (Array.isArray(sourceParentData) && sourceParentData.length > 0) {
        logs.push(`Found ${sourceParentData.length} records for ${parentApiName} (parent).`);
        
        for (const record of sourceParentData) {
          // Deep clone the record to avoid modifying the original
          const recordCopy = JSON.parse(JSON.stringify(record));
          
          // Create a new record with the new project ID and additional data
          const newRecordUnformatted = {
            ...parentAdditionalData,
            ...recordCopy,
            ID: undefined,
            id: undefined,
            _id: undefined,
            projectID: String(newJobId),
            ProjectID: String(newJobId),
          };
          
          // Convert keys to proper format (first char lowercase)
          const newRecord = convertKeysToProperFormat(newRecordUnformatted);
          
          // Ensure projectID is properly formatted
          newRecord.projectId = String(newJobId);
          
          // Post the new parent record
          const parentResponse = await dispatch(postData({
            url: parentUrl,
            data: newRecord,
            contract: { requestCase: 'pascal' },
          })).unwrap();
          // Store the new parent ID for child records
          if (parentResponse && (parentResponse.ID || parentResponse.id)) {
            parentResults.push({
              originalId: record.ID || record.id,
              newId: parentResponse.ID || parentResponse.id,
              // Store whether this parent should have child records by checking the "has" property
              hasChildren: record[hasChildFieldName] === true || 
                         record[hasChildFieldName] === "true" ||
                         record[hasChildFieldName.charAt(0).toUpperCase() + hasChildFieldName.slice(1)] === true ||
                         record[hasChildFieldName.charAt(0).toUpperCase() + hasChildFieldName.slice(1)] === "true"
            });
          }
        }
        
        setStatus((prevStatus) => ({ ...prevStatus, [parentApiName]: "success" }));
        logs.push(`Successfully created ${parentResults.length} ${parentApiName} records.`);
        apiStatuses[parentApiName] = "success";
      } else {
        // If no parent data found, create a default parent record
        logs.push(`No ${parentApiName} data found. Creating default record...`);
        
        // Create default parent record
        const defaultParentUnformatted = {
          projectID: String(newJobId),
          ProjectID: String(newJobId),
          ...parentAdditionalData
        };
        
        const defaultParent = convertKeysToProperFormat(defaultParentUnformatted);
        // Ensure projectID is properly formatted
        defaultParent.projectId = String(newJobId);
        
        const parentResponse = await dispatch(postData({
          url: parentUrl,
          data: defaultParent,
          contract: { requestCase: 'pascal' },
        })).unwrap();
        
        if (parentResponse && (parentResponse.ID || parentResponse.id)) {
          // Since we're creating a default record, use the "has" field from parentAdditionalData
          const hasChildren = parentAdditionalData[hasChildFieldName] === true || 
                           parentAdditionalData[hasChildFieldName] === "true" ||
                           parentAdditionalData[hasChildFieldName.charAt(0).toUpperCase() + hasChildFieldName.slice(1)] === true ||
                           parentAdditionalData[hasChildFieldName.charAt(0).toUpperCase() + hasChildFieldName.slice(1)] === "true";
          
          parentResults.push({
            originalId: null, // No original ID for default record
            newId: parentResponse.ID || parentResponse.id,
            hasChildren: hasChildren
          });
        }
        
        logs.push(`Created default record for ${parentApiName}.`);
        apiStatuses[parentApiName] = "success";
      }
      
      // Now handle child records based on parent results, but only if the parent has children
      if (parentResults.length > 0) {
        // Set status for child
        apiStatuses[childApiName] = "loading";
        setApiStatus({ ...apiStatuses });
        logs.push(`Processing ${childApiName} data (child table)...`);
        
        // Create a mapping of original parent IDs to their child records
        const childrenByParent = {};
        if (Array.isArray(sourceChildData) && sourceChildData.length > 0) {
          logs.push(`Found ${sourceChildData.length} records for ${childApiName} (child).`);
          
          sourceChildData.forEach(child => {
            const parentId = child[childLinkFieldName];
            if (parentId) {
              if (!childrenByParent[parentId]) {
                childrenByParent[parentId] = [];
              }
              childrenByParent[parentId].push(child);
            }
          });
        }
        
        // For each new parent ID, create its associated children ONLY if hasChildren is true
        let childrenCreated = 0;
        for (const parentMapping of parentResults) {
          const originalParentId = parentMapping.originalId;
          const newParentId = parentMapping.newId;
          const hasChildren = parentMapping.hasChildren;
          
          // Skip child creation if this parent shouldn't have children
          if (!hasChildren) {
            logs.push(`Skipping child records for ${childApiName} as parent ${newParentId} has ${hasChildFieldName}=false`);
            continue;
          }
          
          // Find children of this parent (if any)
          const childrenToCreate = originalParentId ? childrenByParent[originalParentId] || [] : [];
          
          if (childrenToCreate.length > 0) {
            logs.push(`Creating ${childrenToCreate.length} child records for ${childApiName} with parent ID: ${newParentId}`);
            
            for (const childRecord of childrenToCreate) {
              // Deep clone the child record
              const childCopy = JSON.parse(JSON.stringify(childRecord));
              
              // Create new child record with correct parent ID reference
              const newChildUnformatted = {
                ...childCopy,
                ID: undefined,
                id: undefined,
                _id: undefined,
                projectID: String(newJobId),
                ProjectID: String(newJobId),
                [childLinkFieldName]: newParentId
              };
              
              // Convert keys to proper format
              const newChild = convertKeysToProperFormat(newChildUnformatted);
              // Ensure projectID and parent ID field are properly formatted
              newChild.projectId = String(newJobId);
              
              // Make sure the childLinkFieldName is in the correct format (first char lowercase)
              const formattedChildLinkFieldName = childLinkFieldName.charAt(0).toLowerCase() + childLinkFieldName.slice(1);
              newChild[formattedChildLinkFieldName] = newParentId;
              
              // Post the new child record
              await dispatch(postData({
                url: childUrl,
                data: newChild,
                contract: { requestCase: 'pascal' },
              })).unwrap();
              childrenCreated++;
            }
          } else {
            // For parent records that should have children but don't have source children,
            // create a default child record
            logs.push(`Creating default child record for ${childApiName} with parent ID: ${newParentId}`);
            
            const defaultChildUnformatted = {
              projectID: String(newJobId),
              ProjectID: String(newJobId),
              [childLinkFieldName]: newParentId
            };
            
            const defaultChild = convertKeysToProperFormat(defaultChildUnformatted);
            defaultChild.projectId = String(newJobId);
            
            // Make sure the childLinkFieldName is in the correct format (first char lowercase)
            const formattedChildLinkFieldName = childLinkFieldName.charAt(0).toLowerCase() + childLinkFieldName.slice(1);
            defaultChild[formattedChildLinkFieldName] = newParentId;
            
            await dispatch(postData({
              url: childUrl,
              data: defaultChild,
              contract: { requestCase: 'pascal' },
            })).unwrap();
            childrenCreated++;
          }
        }
        
        if (childrenCreated > 0) {
          setStatus((prevStatus) => ({ ...prevStatus, [childApiName]: "success" }));
          logs.push(`Successfully created ${childrenCreated} ${childApiName} records.`);
          apiStatuses[childApiName] = "success";
        } else {
          logs.push(`No ${childApiName} records created as no parents have ${hasChildFieldName}=true.`);
          apiStatuses[childApiName] = "skipped";
        }
      } else {
        logs.push(`Unable to create ${childApiName} records because no ${parentApiName} parents were created.`);
        apiStatuses[childApiName] = "skipped";
      }
      
      setApiStatus({ ...apiStatuses });
      return true;
      
    } catch (error) {
      console.error(`Error copying ${parentApiName}/${childApiName} data:`, error);
      logs.push(`Failed to copy ${parentApiName}/${childApiName} data: ${error.message}`);
      apiStatuses[parentApiName] = apiStatuses[parentApiName] === "success" ? "success" : "failed";
      apiStatuses[childApiName] = "failed";
      setApiStatus({ ...apiStatuses });
      return false;
    }
  };

  // Copy data from one API to another with a new projectID using preloaded data
  const copyApiData = async (url, sourceData, newJobId, apiName, logs, apiStatuses, setApiStatus, additionalData = {}) => {
    apiStatuses[apiName] = "loading";
    setApiStatus({ ...apiStatuses });
    logs.push(`Processing ${apiName} data to copy...`);
    
    try {
      if (Array.isArray(sourceData) && sourceData.length > 0) {
        logs.push(`Found ${sourceData.length} records for ${apiName}.`);
        
        for (const record of sourceData) {
          // Deep clone the record to avoid modifying the original
          const recordCopy = JSON.parse(JSON.stringify(record));
          
          // Create a new record with the new project ID and additional data
          const newRecordUnformatted = {
            ...additionalData,
            ...recordCopy,
            ID: undefined,
            id: undefined,
            _id: undefined,
            projectID: String(newJobId),
            ProjectID: String(newJobId),
          };
          
          // Convert keys to proper format (first char lowercase)
          const newRecord = convertKeysToProperFormat(newRecordUnformatted);
          
          // Ensure projectID is properly formatted
          newRecord.projectId = String(newJobId);
          
          // Post the new record
          await dispatch(postData({
            url,
            data: newRecord,
            contract: { requestCase: 'pascal' },
          })).unwrap();
        }
        
        setStatus((prevStatus) => ({ ...prevStatus, [apiName]: "success" }));
        logs.push(`Successfully copied ${apiName} data.`);
        apiStatuses[apiName] = "success";
      } else {
        // If no data found, create a default record with the specified additional data
        logs.push(`No ${apiName} data found to copy. Creating default record...`);
        
        // Create default record and ensure proper key format
        const defaultRecordUnformatted = {
          projectID: String(newJobId),
          ProjectID: String(newJobId),
          ...additionalData
        };
        
        const defaultRecord = convertKeysToProperFormat(defaultRecordUnformatted);
        // Ensure projectID is properly formatted
        defaultRecord.projectId = String(newJobId);
        
        await dispatch(postData({
          url,
          data: defaultRecord,
          contract: { requestCase: 'pascal' },
        })).unwrap();
        
        logs.push(`Created default record for ${apiName}.`);
        apiStatuses[apiName] = "success";
      }
    } catch (error) {
      console.error(`Error copying ${apiName} data:`, error);
      logs.push(`Failed to copy ${apiName} data: ${error.message}`);
      apiStatuses[apiName] = "failed";
    } finally {
      setApiStatus({ ...apiStatuses });
    }
  };

  // Enhanced version of copyJob that preserves source job details
  const copyJob = async (sourceJobId, newJobData, setApiStatus) => {
    const logs = [];
    const apiStatuses = {
      generalProject: "loading",
      generalStation: "idle",
      soldToShipTo: "idle",
      priceBreakdown: "idle",
      ownersManual: "idle",
      hcRequirements: "idle",
      skidConstruction: "idle",
      pumpManifoldPiping: "idle",
      stationEnclosure: "idle",
      dropPipeComments: "idle",
      levelControls: "idle",
      pumps: "idle",
      fertigation: "idle",
      skyHarvester: "idle",
      shipLoose: "idle",
      waterTreatment: "idle",
      inletScreen: "idle",
      inletStrainer: "idle",
      dischargeStrainer: "idle",
      dischargeFilter: "idle",
      roSystem: "idle",
      uvSystem: "idle",
      unregulatedPower: "idle",
      regulatedPowerSupply: "idle",
      sequenceOfOperations: "idle",
      electricalOptions: "idle",
      telemetry: "idle",
      unregulatedBreakers: "idle",
      regulatedBreakers: "idle",
      telemetryAccessories: "idle",
    };

    try {
      // First, preload all data from the source job with client-side filtering
      logs.push("Preloading and filtering source job data...");
      const sourceJobData = await preloadSourceJob(sourceJobId, logs);
      
      // Get original generalProject data for the source job
      const sourceGeneralProject = sourceJobData.generalProject?.find(
        record => String(record.ID || record.id) === String(sourceJobId)
      );
      
      if (!sourceGeneralProject) {
        logs.push("Warning: Could not find source job's general project data");
      }
      
      // Create a new generalProject record by combining source job data with user input
      logs.push("Creating new generalProject record with combined data...");
      
      // Start with source job data (excluding ID fields)
      let combinedData = {};
      if (sourceGeneralProject) {
        const { ID, id, _id, ...sourceDataWithoutIds } = sourceGeneralProject;
        combinedData = { ...sourceDataWithoutIds };
      }
      
      // Override with user input data (this takes precedence)
      const enhancedJobDataUnformatted = {
        ...combinedData,
        ...newJobData,
        // Always ensure these fields use the user input
        jobNumber: newJobData.jobNumber,
        JobNumber: newJobData.jobNumber,
        projectName: newJobData.projectName,
        ProjectName: newJobData.projectName,
      };
      
      // Convert keys to proper format (first char lowercase)
      const enhancedJobData = convertKeysToProperFormat(enhancedJobDataUnformatted);
      
      // Create the new record with combined data
      const response = await dispatch(postData({ 
        url: apiUrls.generalProject, 
        data: enhancedJobData,
        contract: { requestCase: 'pascal' },
      })).unwrap();
      
      setStatus((prevStatus) => ({ ...prevStatus, generalProject: "success" }));
      logs.push("Created new generalProject record successfully with combined data.");
      apiStatuses.generalProject = "success";
      setApiStatus({ ...apiStatuses });

      // Get the ID of the newly created record
      let newJobId;
      if (response && (response.ID || response.id)) {
        // If the API returns the created record directly
        newJobId = response.ID || response.id;
      } else {
        // Otherwise, fetch the latest records and find our new job
        logs.push("Fetching the new job ID...");
        const generalProjectResponse = await dispatch(fetchData({ 
          url: apiUrls.generalProject 
        })).unwrap();
        
        // Find the record that matches our new job data by job number
        const newRecord = generalProjectResponse.find(
          record => String(record.JobNumber || record.jobNumber || "").toLowerCase() === String(newJobData.jobNumber).toLowerCase()
        );
        
        if (newRecord) {
          newJobId = newRecord.ID || newRecord.id;
        } else {
          // Fallback to the last record if match not found
          const lastRecord = generalProjectResponse[generalProjectResponse.length - 1];
          newJobId = lastRecord.ID || lastRecord.id;
        }
      }
      
      logs.push(`Retrieved new job ID: ${newJobId}`);

      // Properly formatted default value objects with camelCase keys
      const defaultValues = {
        skidConstruction: { zone: 1, hasSkid: false },
        pumpManifoldPiping: { zone: 1, hasPiping: false },
        stationEnclosure: { zone: 1, hasEnclosure: false },
        dropPipeComments: { zone: 1, hasDropPipes: false },
        levelControls: { zone: 1, hasLevelControls: false },
        pumps: { zone: 1, hasSustain: false, hasMain: false, hasJockey: false, hasWaterFeature: false, hasTransfer: false, hasRecirc: false, hasTrash: false },
        fertigation: { zone: 1, hasFertigation: false },
        skyHarvester: { zone: 1, hasSky: false },
        shipLoose: { zone: 1, hasShipLoose: false },
        waterTreatment: { zone: 1, hasInletFilter: false, hasInletStrainer: false, hasDischargeFilter: false, hasWyeStrainer: false, hasRO: false, hasUV: false, hasFilters: false },
        unregulatedPower: { zone: 1, hasUnregulated: false },
        regulatedPower: { zone: 1, hasRegulated: false },
        telemetry: { zone: 1, hasTelemetry: false, hasAccessories: false }
      };

      // Standard zone default
      const zoneDefault = { zone: 1 };

      // Copy standard (non-parent/child) tables
      await copyApiData(apiUrls.generalStation, sourceJobData.generalStation, newJobId, "generalStation", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.soldToShipTo, sourceJobData.soldToShipTo, newJobId, "soldToShipTo", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.priceBreakdown, sourceJobData.priceBreakdown, newJobId, "priceBreakdown", logs, apiStatuses, setApiStatus);
      await copyApiData(apiUrls.ownersManual, sourceJobData.ownersManual, newJobId, "ownersManual", logs, apiStatuses, setApiStatus);
      await copyApiData(apiUrls.hcRequirements, sourceJobData.hcRequirements, newJobId, "hcRequirements", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.skidConstruction, sourceJobData.skidConstruction, newJobId, "skidConstruction", logs, apiStatuses, setApiStatus, defaultValues.skidConstruction);
      await copyApiData(apiUrls.pumpManifoldPiping, sourceJobData.pumpManifoldPiping, newJobId, "pumpManifoldPiping", logs, apiStatuses, setApiStatus, defaultValues.pumpManifoldPiping);
      await copyApiData(apiUrls.stationEnclosure, sourceJobData.stationEnclosure, newJobId, "stationEnclosure", logs, apiStatuses, setApiStatus, defaultValues.stationEnclosure);
      await copyApiData(apiUrls.dropPipeComments, sourceJobData.dropPipeComments, newJobId, "dropPipeComments", logs, apiStatuses, setApiStatus, defaultValues.dropPipeComments);
      await copyApiData(apiUrls.levelControls, sourceJobData.levelControls, newJobId, "levelControls", logs, apiStatuses, setApiStatus, defaultValues.levelControls);
      await copyApiData(apiUrls.pumps, sourceJobData.pumps, newJobId, "pumps", logs, apiStatuses, setApiStatus, defaultValues.pumps);
      if (Array.isArray(sourceJobData.pumpDetails) && sourceJobData.pumpDetails.length > 0) {
        await copyApiData(apiUrls.pumpDetails, sourceJobData.pumpDetails, newJobId, "pumpDetails", logs, apiStatuses, setApiStatus);
      } else {
        logs.push("Skipping pumpDetails copy because source job has no pumpDetails records.");
        apiStatuses.pumpDetails = "skipped";
        setApiStatus({ ...apiStatuses });
      }
      await copyApiData(apiUrls.fertigation, sourceJobData.fertigation, newJobId, "fertigation", logs, apiStatuses, setApiStatus, defaultValues.fertigation);
      await copyApiData(apiUrls.skyHarvester, sourceJobData.skyHarvester, newJobId, "skyHarvester", logs, apiStatuses, setApiStatus, defaultValues.skyHarvester);
      await copyApiData(apiUrls.shipLoose, sourceJobData.shipLoose, newJobId, "shipLoose", logs, apiStatuses, setApiStatus, defaultValues.shipLoose);
      await copyApiData(apiUrls.waterTreatment, sourceJobData.waterTreatment, newJobId, "waterTreatment", logs, apiStatuses, setApiStatus, defaultValues.waterTreatment);
      await copyApiData(apiUrls.inletScreen, sourceJobData.inletScreen, newJobId, "inletScreen", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.inletStrainer, sourceJobData.inletStrainer, newJobId, "inletStrainer", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.dischargeStrainer, sourceJobData.dischargeStrainer, newJobId, "dischargeStrainer", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.dischargeFilter, sourceJobData.dischargeFilter, newJobId, "dischargeFilter", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.roSystem, sourceJobData.roSystem, newJobId, "roSystem", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.uvSystem, sourceJobData.uvSystem, newJobId, "uvSystem", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.sequenceOfOperations, sourceJobData.sequenceOfOperations, newJobId, "sequenceOfOperations", logs, apiStatuses, setApiStatus, zoneDefault);
      await copyApiData(apiUrls.electricalOptions, sourceJobData.electricalOptions, newJobId, "electricalOptions", logs, apiStatuses, setApiStatus, zoneDefault);
      logs.push("Skipping selectedPumpOptionList copy because this is a GET-only table.");
      apiStatuses.selectedPumpOptionList = "skipped";
      setApiStatus({ ...apiStatuses });

      logs.push("Skipping customOptions copy because this is a GET-only table.");
      apiStatuses.customOptions = "skipped";
      setApiStatus({ ...apiStatuses });
      await copyApiData(apiUrls.controlLogicOnly, sourceJobData.controlLogicOnly, newJobId, "controlLogicOnly", logs, apiStatuses, setApiStatus, { zone: 1, hasControlLogic: false });

      // Handle parent-child relationships
      // 1. Unregulated Power & Breakers
      await copyParentChildData(
        apiUrls.unregulatedPower,
        apiUrls.unregulatedBreakers,
        sourceJobData.unregulatedPower,
        sourceJobData.unregulatedBreakers,
        newJobId,
        "unregulatedPower",
        "unregulatedBreakers",
        logs,
        apiStatuses,
        setApiStatus,
        defaultValues.unregulatedPower,
        "SupplyParentID",
        "hasUnregulated"  // The field that determines if child records should be created
      );

      // 2. Regulated Power & Breakers
      await copyParentChildData(
        apiUrls.regulatedPower,
        apiUrls.regulatedBreakers,
        sourceJobData.regulatedPower,
        sourceJobData.regulatedBreakers,
        newJobId,
        "regulatedPower",
        "regulatedBreakers",
        logs,
        apiStatuses,
        setApiStatus,
        defaultValues.regulatedPower,
        "SupplyParentID",
        "hasRegulated"  // The field that determines if child records should be created
      );

      // 3. Telemetry & Accessories
      await copyParentChildData(
        apiUrls.telemetry,
        apiUrls.telemetryAccessories,
        sourceJobData.telemetry,
        sourceJobData.telemetryAccessories,
        newJobId,
        "telemetry",
        "telemetryAccessories",
        logs,
        apiStatuses,
        setApiStatus,
        defaultValues.telemetry,
        "TelemetryParentID",
        "hasAccessories"  // The field that determines if child records should be created
      );

      return { jobId: newJobId, logs, apiStatuses };
    } catch (error) {
      console.error("Error copying job:", error);
      setStatus((prevStatus) => ({ ...prevStatus, [error.api || "unknown"]: "failed" }));
      logs.push(`Failed to copy job: ${error.message}`);
      apiStatuses[error.api || "unknown"] = "failed";
      setApiStatus({ ...apiStatuses });
      const copyError = new Error(`Failed to copy job: ${error.message}`);
      copyError.originalError = error;
      copyError.logs = logs;
      copyError.apiStatuses = apiStatuses;
      throw copyError;
    }
  };

  return { searchJobs, copyJob, status, searchResults, isSearching };
};

export default useCopyJob;
